import { xchacha20poly1305 } from "@noble/ciphers/chacha.js";
import { managedNonce } from "@noble/ciphers/utils.js";
import { apiBaseUrl, gatewayHeaders } from "@/config/api";

const PREFIX = "enc2:";
const PENDING = "enc2:pending";
const KID_NAME = "secureStorage_kid";
const PROTECTED_NAMES = [
  "wrapperData",
  "userData",
  "currentRoom",
  "chatRooms",
  "notifications",
  "requests",
  "Serverrequests",
  "msrp_message_queue",
];
const STARTUP_TIMEOUT_MS = 4000;
const RETRY_AFTER_MS = 30000;

type StorageKey = { kid: string; key: Uint8Array };

let current: StorageKey | null = null;
let inFlight: Promise<StorageKey | null> | null = null;
let failedAt = 0;
const pending = new Map<Storage, Map<string, string>>();

const cipher = managedNonce(xchacha20poly1305);
const encoder = new TextEncoder();
const decoder = new TextDecoder();

const attempt = (fn: () => void): void => {
  try {
    fn();
  } catch {
    return;
  }
};

const toBase64 = (bytes: Uint8Array): string => {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
  }
  return btoa(binary);
};

const fromBase64 = (text: string): Uint8Array => Uint8Array.from(atob(text), (c) => c.charCodeAt(0));

const stores = (): Storage[] => {
  const list: Storage[] = [];
  attempt(() => list.push(localStorage));
  attempt(() => list.push(sessionStorage));
  return list;
};

const pendingFor = (store: Storage): Map<string, string> => {
  let map = pending.get(store);
  if (!map) {
    map = new Map();
    pending.set(store, map);
  }
  return map;
};

const encrypt = (k: StorageKey, name: string, plain: string): string =>
  `${PREFIX}${k.kid}:${toBase64(cipher(k.key, encoder.encode(name)).encrypt(encoder.encode(plain)))}`;

const decrypt = (k: StorageKey | null, name: string, raw: string): string | null => {
  const sep = raw.indexOf(":", PREFIX.length);
  if (!k || sep === -1 || raw.slice(PREFIX.length, sep) !== k.kid) return null;
  try {
    return decoder.decode(cipher(k.key, encoder.encode(name)).decrypt(fromBase64(raw.slice(sep + 1))));
  } catch {
    return null;
  }
};

const storeEach = (fn: (store: Storage, name: string, raw: string) => void): void => {
  for (const store of stores()) {
    const names: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const name = store.key(i);
      if (name !== null) names.push(name);
    }
    for (const name of names) {
      const raw = store.getItem(name);
      if (raw !== null) fn(store, name, raw);
    }
  }
};

const protectPlainValues = (): boolean => {
  let found = false;
  for (const store of stores()) {
    for (const name of PROTECTED_NAMES) {
      const raw = store.getItem(name);
      if (raw === null) continue;
      found = true;
      if (raw.startsWith(PREFIX)) continue;
      pendingFor(store).set(name, raw);
      store.setItem(name, PENDING);
    }
  }
  return found;
};

const flushPending = (k: StorageKey): void => {
  pending.forEach((map, store) => {
    map.forEach((value, name) => {
      if (store.getItem(name) === PENDING) store.setItem(name, encrypt(k, name, value));
    });
    map.clear();
  });
};

const rekey = (from: StorageKey | null, to: StorageKey): void => {
  if (!from || from.kid === to.kid) return;
  storeEach((store, name, raw) => {
    if (raw === PENDING || !raw.startsWith(PREFIX)) return;
    const plain = decrypt(from, name, raw);
    if (plain !== null) store.setItem(name, encrypt(to, name, plain));
  });
};

const requestKey = async (): Promise<StorageKey | null> => {
  const init: RequestInit = { credentials: "include", headers: gatewayHeaders() };

  const read = async (): Promise<StorageKey | null | "unauthorized"> => {
    const res = await fetch(`${apiBaseUrl}/authentication/storage-key`, init);
    if (res.status === 401) return "unauthorized";
    if (!res.ok) return null;
    const body = (await res.json()) as { key: string; kid: string };
    return { kid: body.kid, key: fromBase64(body.key) };
  };

  try {
    const first = await read();
    if (first !== "unauthorized") return first;
    const refreshed = await fetch(`${apiBaseUrl}/authentication/refresh-token`, { ...init, method: "POST" });
    if (!refreshed.ok) return null;
    const second = await read();
    return second === "unauthorized" ? null : second;
  } catch {
    return null;
  }
};

const loadKey = (force = false): Promise<StorageKey | null> => {
  if (!inFlight && !force && Date.now() - failedAt < RETRY_AFTER_MS) return Promise.resolve(current);
  if (!inFlight) {
    inFlight = requestKey()
      .then((next) => {
        if (!next) {
          failedAt = Date.now();
          return current;
        }
        failedAt = 0;
        rekey(current, next);
        current = next;
        attempt(() => localStorage.setItem(KID_NAME, next.kid));
        flushPending(next);
        return next;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

export const initSecureStorage = async (): Promise<void> => {
  let needed = false;
  attempt(() => {
    needed = protectPlainValues();
    window.addEventListener("storage", (event) => {
      if (event.key === KID_NAME && event.newValue && event.newValue !== current?.kid) void loadKey(true);
    });
  });
  if (!needed) return;
  await Promise.race([loadKey(true), new Promise((resolve) => setTimeout(resolve, STARTUP_TIMEOUT_MS))]);
};

export const refreshStorageKey = async (): Promise<void> => {
  await loadKey(true);
};

export const secureSet = (store: Storage, name: string, value: string): void => {
  if (current) {
    pending.get(store)?.delete(name);
    store.setItem(name, encrypt(current, name, value));
    return;
  }
  pendingFor(store).set(name, value);
  store.setItem(name, PENDING);
  void loadKey();
};

export const secureGet = (store: Storage, name: string): string | null => {
  const raw = store.getItem(name);
  if (raw === null) {
    pending.get(store)?.delete(name);
    return null;
  }
  if (raw === PENDING) return pending.get(store)?.get(name) ?? null;
  if (raw.startsWith(PREFIX)) return decrypt(current, name, raw);
  return raw;
};
